import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { Badge } from '@neo/primitives/badge'
import { Button } from '@neo/primitives/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Typography } from '@neo/primitives/typography'
import { ArrowRight, ChevronRight, X } from 'lucide-react'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { filterUserTags } from './scenario-tags'
import type { PersistedScenario } from './simulation-tab.queries'

const SCENARIO_TYPE_LABEL: Record<string, string> = {
  clean_baseline: 'Clean baseline',
  messy_success: 'Messy success',
  recovery_success: 'Recovery success',
  boundary_pressure: 'Boundary pressure',
}

const SMOKE_KIND_LABEL: Record<string, string> = {
  happy_path: 'Happy path',
  boundary: 'Boundary',
}

const MAX_TRAITS_INLINE = 4

const titleCase = (id: string | undefined): string => {
  if (!id) return ''
  return id
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

const formatPercent = (probability: number | undefined): string =>
  probability != null ? `${Math.round(probability * 100)}%` : ''

const isMode = (value: unknown): value is 'smoke' | 'stress' =>
  value === 'smoke' || value === 'stress'

interface Props {
  scenario: PersistedScenario
  showJsonTab: boolean
  onClose: () => void
}

export const ScenarioDetailsSidebar = ({ scenario, showJsonTab, onClose }: Props) => {
  const s = scenario.scenario
  const meta = s.eval_metadata ?? {}
  const simulationMode = isMode(meta.simulation_mode) ? meta.simulation_mode : 'stress'
  const focusLabel =
    simulationMode === 'smoke'
      ? (SMOKE_KIND_LABEL[meta.smoke_unit_kind ?? ''] ?? titleCase(meta.smoke_unit_kind))
      : (SCENARIO_TYPE_LABEL[meta.scenario_type ?? ''] ?? titleCase(meta.scenario_type))

  return (
    <aside className="flex min-h-0 w-[400px] flex-shrink-0 flex-col border-l border-border bg-card">
      <SidebarHeader
        name={s.name}
        simulationMode={simulationMode}
        focusLabel={focusLabel}
        onClose={onClose}
      />
      {showJsonTab ? (
        <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0">
          <TabsList className="px-4 shrink-0">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="json">Raw JSON</TabsTrigger>
          </TabsList>
          <TabsContent value="details" className="flex-1 min-h-0 mt-0 outline-none">
            <ScrollArea className="h-full">
              <DetailsBody scenario={scenario} simulationMode={simulationMode} />
            </ScrollArea>
          </TabsContent>
          <TabsContent value="json" className="flex-1 min-h-0 mt-0 outline-none">
            <JsonBody scenario={scenario} />
          </TabsContent>
        </Tabs>
      ) : (
        <ScrollArea className="flex-1 min-h-0">
          <DetailsBody scenario={scenario} simulationMode={simulationMode} />
        </ScrollArea>
      )}
    </aside>
  )
}

interface HeaderProps {
  name?: string
  simulationMode: 'smoke' | 'stress'
  focusLabel: string
  onClose: () => void
}

const SidebarHeader = ({ name, simulationMode, focusLabel, onClose }: HeaderProps) => (
  <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
    <div className="min-w-0 flex-1">
      <Typography variant="P/400" className="truncate" title={name}>
        {name ?? 'Untitled scenario'}
      </Typography>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <Badge variant="tag">{titleCase(simulationMode)}</Badge>
        {focusLabel && <Badge variant="tag">{focusLabel}</Badge>}
      </div>
    </div>
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 -mt-0.5"
      onClick={onClose}
      data-testid="button-close-scenario-details"
    >
      <X className="h-4 w-4" />
    </Button>
  </div>
)

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="XS/500" className="font-jetbrains text-label text-secondary uppercase">
    {children}
  </Typography>
)

const Section = ({
  label,
  children,
  testId,
}: {
  label: string
  children: React.ReactNode
  testId?: string
}) => (
  <section className="border-b border-border px-4 py-3 last:border-b-0" data-testid={testId}>
    <SectionLabel>{label}</SectionLabel>
    <div className="mt-2">{children}</div>
  </section>
)

interface BodyProps {
  scenario: PersistedScenario
  simulationMode?: 'smoke' | 'stress'
}

export const ScenarioDetailsBody = ({ scenario, simulationMode }: BodyProps) => {
  const effectiveMode =
    simulationMode ??
    (isMode(scenario.scenario.eval_metadata?.simulation_mode)
      ? scenario.scenario.eval_metadata?.simulation_mode
      : 'stress')
  return <DetailsBody scenario={scenario} simulationMode={effectiveMode} />
}

const DetailsBody = ({ scenario, simulationMode }: Required<BodyProps>) => {
  const s = scenario.scenario
  const meta = s.eval_metadata ?? {}
  const expectedIntent = meta.expected_route_outcome?.expected_intent_name
  const expectedNodeType = meta.expected_route_outcome?.target_node_type
  const patternId = meta.conversation_pattern_id
  const userTags = filterUserTags(s.tags)
  const persona = s.persona ?? { personality: '' }
  const personaSummary = [persona.personality, persona.emotional_state].filter(Boolean).join(', ')
  const traits = (persona.behavioral_traits ?? []).slice(0, MAX_TRAITS_INLINE)
  const personaDetails = (persona.details ?? {}) as Record<string, unknown>
  const personaDetailEntries = Object.entries(personaDetails).filter(
    ([, v]) => v != null && v !== '',
  )

  const conditions = [
    patternId && { label: `Pattern: ${titleCase(patternId)}`, variant: 'default' as const },
    s.interruption?.enabled && {
      label: `Interruptions ${formatPercent(s.interruption.probability)}`,
      variant: 'orange' as const,
    },
    s.stt_noise?.enabled && {
      label: `STT noise: ${s.stt_noise.severity}`,
      variant: 'orange' as const,
    },
    s.non_answer?.enabled && {
      label: `Non-answer ${formatPercent(s.non_answer.probability)}`,
      variant: 'orange' as const,
    },
  ].filter((c): c is { label: string; variant: 'default' | 'orange' } => Boolean(c))

  const smokeDescription = simulationMode === 'smoke' ? meta.smoke_unit_description : undefined

  return (
    <div className="flex flex-col">
      {smokeDescription && (
        <Section label="Test focus" testId="section-test-focus">
          <Typography variant="S/400" className="text-foreground">
            {smokeDescription}
          </Typography>
        </Section>
      )}

      <Section label="Goal" testId="section-goal">
        <Typography variant="S/400" className="text-foreground whitespace-pre-wrap">
          {s.goal}
        </Typography>
      </Section>

      {expectedIntent && (
        <Section label="Expected outcome" testId="section-expected-outcome">
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary shrink-0" />
            <Typography variant="P/400" className="text-foreground">
              {expectedIntent}
            </Typography>
          </div>
          {expectedNodeType && (
            <Typography variant="XS/400" className="text-secondary mt-1 ml-6">
              ends in {expectedNodeType}
            </Typography>
          )}
        </Section>
      )}

      <Section label="Caller" testId="section-caller">
        {personaSummary && (
          <Typography variant="S/400" className="text-foreground capitalize">
            {personaSummary}
          </Typography>
        )}
        {s.language && (
          <Typography variant="XS/400" className="text-secondary mt-1">
            {s.language}
          </Typography>
        )}
        {traits.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {traits.map((t) => (
              <Badge key={t} variant="tag">
                {t.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      {conditions.length > 0 && (
        <Section label="Conditions" testId="section-conditions">
          <div className="flex flex-wrap gap-1.5">
            {conditions.map((c) => (
              <Badge key={c.label} variant={c.variant}>
                {c.label}
              </Badge>
            ))}
          </div>
        </Section>
      )}

      {userTags.length > 0 && (
        <Section label="Tags" testId="section-tags">
          <div className="flex flex-wrap gap-1.5">
            {userTags.map((t) => (
              <Badge key={t} variant="tag" className="text-secondary">
                {t.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </Section>
      )}

      {personaDetailEntries.length > 0 && (
        <Section label="Caller details" testId="section-caller-details">
          <Collapsible>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group flex items-center gap-1 font-jetbrains text-label uppercase text-secondary hover:text-foreground transition-colors"
                data-testid="button-toggle-caller-details"
              >
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-90" />
                Show {personaDetailEntries.length}{' '}
                {personaDetailEntries.length === 1 ? 'field' : 'fields'}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5">
                {personaDetailEntries.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-xs-400 text-secondary capitalize">
                      {k.replace(/_/g, ' ')}
                    </dt>
                    <dd className="text-xs-400 text-foreground break-words">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </CollapsibleContent>
          </Collapsible>
        </Section>
      )}

      <Section label="Scenario UUID" testId="section-uuid">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <Typography
            variant="XS/400"
            className="text-secondary font-jetbrains tabular-nums truncate"
          >
            {scenario.uuid}
          </Typography>
          <ClipboardCopyButton
            textToCopy={scenario.uuid}
            tooltipText="Copy UUID"
            iconSize={12}
            className="h-7 w-7 shrink-0"
          />
        </div>
      </Section>
    </div>
  )
}

const JsonBody = ({ scenario }: { scenario: PersistedScenario }) => {
  const json = JSON.stringify(scenario, null, 2)
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2">
        <Typography variant="S/600" className="text-secondary">
          Raw scenario JSON
        </Typography>
        <ClipboardCopyButton
          textToCopy={json}
          tooltipText="Copy JSON"
          iconSize={12}
          className="h-7 w-7"
          data-testid="button-copy-scenario-json"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <CodeEditor
          className="h-full [&_.cm-editor]:h-full [&_.cm-scroller]:h-full [&_.cm-editor]:bg-background"
          value={json}
          readOnly
          lineWrapping
          showLineNumbers={false}
          onChange={() => {}}
        />
      </div>
    </div>
  )
}
